import React from 'react';
import { getOptimizedImage } from '../../utils/images';

interface ResponsiveImageProps {
  /** The image's original public path, e.g. "/ProfilePic/Sid_Profile.jpg". */
  path: string;
  alt: string;
  /** Load immediately with high priority (only for the hero image). */
  priority?: boolean;
  className?: string;
}

/**
 * Serves the optimized WebP (with a JPEG or PNG fallback) at 1x/2x density from
 * scripts/optimize-images.js, lazy-loaded unless marked as priority. Images that have not
 * been optimized yet fall back to the original file.
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({ path, alt, priority = false, className }) => {
  const optimized = getOptimizedImage(path);
  const loadingProps = {
    loading: priority ? ('eager' as const) : ('lazy' as const),
    decoding: 'async' as const,
    ...(priority ? ({ fetchpriority: 'high' } as Record<string, string>) : {}),
  };

  if (!optimized) {
    return <img src={path} alt={alt} className={className} {...loadingProps} />;
  }

  const { base, fallback, width, height } = optimized;
  return (
    <picture>
      <source type="image/webp" srcSet={`${base}-1x.webp 1x, ${base}-2x.webp 2x`} />
      <img
        src={`${base}-1x.${fallback}`}
        srcSet={`${base}-1x.${fallback} 1x, ${base}-2x.${fallback} 2x`}
        alt={alt}
        width={width}
        height={height}
        className={className}
        {...loadingProps}
      />
    </picture>
  );
};

export default ResponsiveImage;
